import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as FileSaver from 'file-saver';

import { BasePage } from '../../base.page';
import { Client, Contract, Document } from '../../../domain/data';
import {
    ClientType,
    DocumentTypeId,
    GetContractUseCase,
    GetDocumentContentUseCase,
    GetParticipantsUseCase,
    UpdateContractUseCase,
    UserInterface,
} from '../../../../core';
import { ConfirmDialog } from '../../../ui/dialog/confirm/confirm.dialog';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { MainRoutes } from '../../../main-routes.enum';
import { ParentEvents } from '../../../utils/parent-events.service';
import { ViewPage } from '../view.page';

@Component({
    templateUrl: './view-account.page.html',
    styleUrls: ['./view-account.page.scss'],
})
export class ViewAccountPage extends BasePage implements AfterViewInit, OnDestroy, OnInit {
    public MainRoutes: typeof MainRoutes = MainRoutes;
    public ClientType: typeof ClientType = ClientType;

    private backSubscription: Subscription;

    public editing = false;
    public contract: Contract;
    public participants: Client[] = [];
    public isFormFullDataAccountValid = false;

    public ibanOriginal: string;
    public requiredDocumentProof = false;
    public manualDocumentProofRemove = false;

    @ViewChild('formFullDataAccount')
    private form: NgForm;
    private formSubscription;

    constructor(
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private getContractUseCase: GetContractUseCase,
        private getDocumentContentUseCase: GetDocumentContentUseCase,
        private getParticipantsUseCase: GetParticipantsUseCase,
        private updateContractUseCase: UpdateContractUseCase,
        private viewPage: ViewPage
    ) {
        super(parentEvents);
        viewPage.showBackButton();
        this.backSubscription = viewPage.back.subscribe(() => {
            if (this.editing) {
                const dialogRef = this.dialog.open(ConfirmDialog, {
                    data: { text: 'dialog.loss-data' },
                });

                dialogRef.afterClosed().subscribe((result) => {
                    if (result) {
                        this.parentEvents.cancel(true);
                    }
                });
            } else {
                this.parentEvents.cancel(true);
            }
        });
    }

    get user(): UserInterface {
        return this.viewPage.user;
    }

    get canEdit(): boolean {
        return this.user.canEdit;
    }

    ngOnInit() {
        this.getContract();
        this.getParticipants();
    }

    private getContract() {
        this.getContractUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.contract = transaction.data as Contract;
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    private getParticipants() {
        this.getParticipantsUseCase.bind({ isFull: false }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.participants = transaction.data as Client[];
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    ngOnDestroy() {
        this.backSubscription.unsubscribe();
    }

    onAccordionChange() {
        this.parentEvents.resize();
    }

    onEdit() {
        this.editing = true;
        this.ibanOriginal = this.contract.iban;

        if (this.form && !this.formSubscription) {
            this.formSubscription = this.form.statusChanges.subscribe(() => {
                setTimeout(() => {
                    this.isFormFullDataAccountValid = this.form.valid;
                });
            });
        }
    }

    onIbanChange() {
        this.contract.documents.map((document) => {
            if (String(document.typeId) === DocumentTypeId.CONTRACT_PROOF) {
                if (this.contract.iban !== this.ibanOriginal && !document.file) {
                    this.requiredDocumentProof = true;
                    document.remove = true;
                } else if (!this.manualDocumentProofRemove) {
                    this.requiredDocumentProof = false;
                    document.remove = false;
                } else {
                    this.requiredDocumentProof = false;
                }
            }
        });
    }

    onCancel() {
        const dialogRef = this.dialog.open(ConfirmDialog, {
            data: { text: 'dialog.loss-data' },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.editing = false;
                this.getContract();
            }
        });
    }

    onSubmit() {
        const dialogRef = this.dialog.open(ConfirmDialog, {
            data: { text: 'dialog.save-data' },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.updateContractUseCase.bind({ contract: this.contract }).subscribe((transaction) => {
                    if (transaction.isSuccess) {
                        this.editing = false;
                        this.getContract();
                    } else if (!transaction.isAuthError) {
                        this.dialog.open(ErrorDialog, {
                            data: { text: transaction.error },
                        });
                    }
                });
            }
        });
    }

    onDocumentView(document: Document) {
        this.getDocumentContentUseCase.bind({ document }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                FileSaver.saveAs(transaction.data, document.filename);
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    onDocumentRemove(document: Document) {
        if (document.id) {
            document.remove = true;

            if (String(document.typeId) === DocumentTypeId.CONTRACT_PROOF) {
                this.manualDocumentProofRemove = true;
            }
        }
    }
}
